import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { searchSites, type SearchSite } from '@/mocks/searchData';
import supabase from '@/lib/supabase';

type StatusFilter = 'all' | 'safe' | 'warning' | 'scam';
type SortKey = 'latest' | 'reports' | 'name';

function formatVerfiedDate(dateStr: string): string {
  try {
    if (dateStr.includes('.')) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[0]}.${parts[1]}.${parts[2]}`;
    return dateStr;
  } catch {
    return dateStr;
  }
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('latest');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [sites, setSites] = useState<SearchSite[]>(searchSites);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data, error } = await supabase
        .from('verified_sites')
        .select('*')
        .order('verified_date', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const formatted: SearchSite[] = data.map(
          (row: Record<string, unknown>) => ({
            id: String(row.id),
            name: row.name as string,
            domain: row.domain as string,
            status: row.status as SearchSite['status'],
            reportCount: (row.report_count as number) || 0,
            verifiedDate: formatVerfiedDate(row.verified_date as string),
            category: row.category as string,
            tags: (row.tags as string[]) || [],
          })
        );
        setSites(formatted);
      } else {
        setSites(searchSites);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setFetchError(message);
      setSites(searchSites);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [query, setSearchParams]);

  useEffect(() => {
    const paramQ = searchParams.get('q') || '';
    if (paramQ !== query) {
      setQuery(paramQ);
    }
  }, [searchParams]);

  const categories = useMemo(() => {
    const cats = new Set(sites.map((s) => s.category));
    return ['all', ...Array.from(cats)];
  }, [sites]);

  const filteredResults = useMemo(() => {
    let results = [...sites];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      results = results.filter(
        (s) => s.name.toLowerCase().includes(q) || s.domain.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      results = results.filter((s) => s.status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      results = results.filter((s) => s.category === categoryFilter);
    }

    switch (sortBy) {
      case 'reports':
        results.sort((a, b) => b.reportCount - a.reportCount);
        break;
      case 'name':
        results.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        break;
      default:
        results.sort((a, b) => b.verifiedDate.localeCompare(a.verifiedDate));
    }

    return results;
  }, [sites, query, statusFilter, categoryFilter, sortBy]);

  const statusBadge = (status: SearchSite['status']) => {
    switch (status) {
      case 'safe':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap"><i className="ri-shield-check-line text-xs" />안전</span>;
      case 'warning':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 whitespace-nowrap"><i className="ri-error-warning-line text-xs" />주의</span>;
      case 'scam':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap"><i className="ri-close-circle-line text-xs" />먹튀</span>;
    }
  };

  const statusSummary = useMemo(() => {
    const total = sites.length;
    const safeCount = sites.filter((s) => s.status === 'safe').length;
    const warningCount = sites.filter((s) => s.status === 'warning').length;
    const scamCount = sites.filter((s) => s.status === 'scam').length;
    return { total, safeCount, warningCount, scamCount };
  }, [sites]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        <div className="bg-secondary-950">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">먹튀 사이트 검색</h1>
            <p className="text-sm text-foreground-400">
              사이트명 또는 도메인으로 먹튀 이력과 검증 결과를 확인하세요
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="사이트명 또는 도메인을 입력하세요"
                  className="flex-1 bg-white text-foreground-950 text-sm px-4 py-3 rounded-l-md border border-foreground-200 focus:outline-none focus:border-primary-500"
                />
                <button className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-r-md transition-colors whitespace-nowrap flex items-center gap-2">
                  <i className="ri-search-line" />
                  <span className="hidden sm:inline">검색</span>
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-secondary-900 rounded-md p-4 text-center">
                <p className="text-2xl font-bold text-white">{loading ? '...' : statusSummary.total}</p>
                <p className="text-xs text-foreground-400 mt-1">전체 등록</p>
              </div>
              <div className="bg-secondary-900 rounded-md p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{loading ? '...' : statusSummary.safeCount}</p>
                <p className="text-xs text-foreground-400 mt-1">안전</p>
              </div>
              <div className="bg-secondary-900 rounded-md p-4 text-center">
                <p className="text-2xl font-bold text-yellow-400">{loading ? '...' : statusSummary.warningCount}</p>
                <p className="text-xs text-foreground-400 mt-1">주의</p>
              </div>
              <div className="bg-secondary-900 rounded-md p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{loading ? '...' : statusSummary.scamCount}</p>
                <p className="text-xs text-foreground-400 mt-1">먹튀확정</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {fetchError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md px-4 py-3 mb-4 flex items-center gap-2">
              <i className="ri-error-warning-line text-yellow-600" />
              <p className="text-sm text-yellow-700">
                서버 연결에 실패했습니다. 저장된 데이터를 표시합니다.{' '}
                <button onClick={fetchSites} className="underline font-medium hover:text-yellow-800">
                  다시 시도
                </button>
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 whitespace-nowrap">상태:</span>
              {(['all', 'safe', 'warning', 'scam'] as StatusFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    statusFilter === f
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {f === 'all' ? '전체' : f === 'safe' ? '안전' : f === 'warning' ? '주의' : '먹튀'}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 whitespace-nowrap">분류:</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border-0 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c === 'all' ? '전체' : c}
                  </option>
                ))}
              </select>

              <span className="text-sm text-foreground-600 ml-3 whitespace-nowrap">정렬:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortKey)}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border-0 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
              >
                <option value="latest">최신순</option>
                <option value="reports">신고 많은순</option>
                <option value="name">이름순</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
            {loading ? (
              <div className="py-20 text-center">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-foreground-500">검증 데이터를 불러오는 중...</p>
              </div>
            ) : filteredResults.length === 0 ? (
              <div className="py-20 text-center">
                <i className="ri-search-line text-4xl text-foreground-300 mb-3 block" />
                <p className="text-foreground-600 font-medium">검색 결과가 없습니다</p>
                <p className="text-sm text-foreground-400 mt-1">다른 검색어나 필터로 다시 시도해보세요</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-foreground-200 bg-background-100">
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">상태</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">사이트명</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">도메인</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">분류</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">신고</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">검증일</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">태그</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((site) => (
                      <tr
                        key={site.id}
                        onClick={() => navigate(`/site/${site.id}`)}
                        className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">{statusBadge(site.status)}</td>
                        <td className="px-4 py-3">
                          <Link to={`/site/${site.id}`} onClick={(e) => e.stopPropagation()} className="text-sm font-medium text-foreground-950 hover:text-primary-500 transition-colors">{site.name}</Link>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground-500">{site.domain}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground-600">{site.category}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-medium ${site.reportCount >= 50 ? 'text-red-600' : 'text-foreground-600'}`}>
                            {site.reportCount}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground-500 whitespace-nowrap">{site.verifiedDate}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {site.tags.map((tag) => (
                              <span
                                key={tag}
                                className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                                  tag.includes('먹튀')
                                    ? 'bg-red-50 text-red-600'
                                    : tag.includes('안전')
                                    ? 'bg-green-50 text-green-600'
                                    : 'bg-yellow-50 text-yellow-600'
                                }`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-4 text-center text-sm text-foreground-500">
            총 <strong className="text-foreground-950">{filteredResults.length}</strong>개의 검색 결과
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}